/**
 * Service de gestion des autorisations d'appareils
 */

import { supabase } from './supabaseClient';
import {
  getOrCreateDeviceFingerprint,
  getDeviceInfo,
  generateDeviceName,
} from '../utils/deviceFingerprint';

export interface AuthorizedDevice {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string | null;
  device_info: any;
  ip_address: string | null;
  authorized_by: string | null;
  authorized_at: string;
  last_used_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DeviceAuthorizationRequest {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string | null;
  device_info: any;
  ip_address: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    email: string;
    prenom: string;
    nom: string;
  };
}

/**
 * Vérifie si l'appareil actuel est autorisé pour l'utilisateur
 */
export async function checkDeviceAuthorization(
  userId: string
): Promise<{ authorized: boolean; fingerprint: string }> {
  const fingerprint = await getOrCreateDeviceFingerprint();

  const { data, error } = await supabase.rpc('is_device_authorized', {
    p_user_id: userId,
    p_device_fingerprint: fingerprint,
  });

  if (error) {
    console.error("Erreur lors de la vérification de l'appareil:", error);
    throw error;
  }

  return {
    authorized: data === true,
    fingerprint,
  };
}

/**
 * Met à jour la dernière utilisation de l'appareil
 */
export async function updateDeviceLastUsed(userId: string, fingerprint: string): Promise<void> {
  const { error } = await supabase.rpc('update_device_last_used', {
    p_user_id: userId,
    p_device_fingerprint: fingerprint,
  });

  if (error) {
    console.error("Erreur lors de la mise à jour de l'appareil:", error);
  }
}

/**
 * Crée une demande d'autorisation pour l'appareil actuel
 */
export async function createDeviceAuthorizationRequest(
  userId: string
): Promise<DeviceAuthorizationRequest> {
  const fingerprint = await getOrCreateDeviceFingerprint();
  const deviceInfo = getDeviceInfo();
  const deviceName = generateDeviceName();

  // Vérifier si une demande existe déjà pour cet appareil
  const { data: existingRequest } = await supabase
    .from('device_authorization_requests')
    .select('*')
    .eq('user_id', userId)
    .eq('device_fingerprint', fingerprint)
    .eq('status', 'pending')
    .single();

  if (existingRequest) {
    return existingRequest;
  }

  // Créer une nouvelle demande
  const { data, error } = await supabase
    .from('device_authorization_requests')
    .insert({
      user_id: userId,
      device_fingerprint: fingerprint,
      device_name: deviceName,
      device_info: deviceInfo,
      ip_address: null, // On pourrait récupérer l'IP via une API externe si nécessaire
    })
    .select()
    .single();

  if (error) {
    console.error('Erreur lors de la création de la demande:', error);
    throw error;
  }

  // Envoyer l'email de notification au gérant
  try {
    await supabase.functions.invoke('send-device-authorization-email', {
      body: {
        requestId: data.id,
        userId: userId,
        deviceName: deviceName,
        deviceInfo: deviceInfo,
      },
    });
  } catch (emailError) {
    console.error("⚠️ Erreur lors de l'envoi de l'email:", emailError);
    // Ne pas bloquer si l'email échoue
  }

  return data;
}

/**
 * Récupère les demandes en attente pour l'utilisateur connecté
 */
export async function getPendingRequests(): Promise<DeviceAuthorizationRequest[]> {
  // Récupérer les demandes en attente
  const { data: requests, error } = await supabase
    .from('device_authorization_requests')
    .select('*')
    .eq('status', 'pending')
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Erreur lors de la récupération des demandes:', error);
    throw error;
  }

  if (!requests || requests.length === 0) {
    return [];
  }

  // Récupérer les infos utilisateurs via auth_user_id et filtrer les admins
  const enrichedRequests = await Promise.all(
    requests.map(async request => {
      const { data: userData } = await supabase
        .from('users')
        .select('email, prenom, nom, roles(nom)')
        .eq('auth_user_id', request.user_id)
        .single();

      return {
        ...request,
        user: userData || undefined,
        userRole: (userData?.roles as any)?.nom,
      };
    })
  );

  // Filtrer les admins et Super Administrateurs
  return enrichedRequests.filter(
    req => req.userRole !== 'admin' && req.userRole !== 'Super Administrateur'
  );
}

/**
 * Récupère le nombre de demandes en attente
 */
export async function getPendingRequestsCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_pending_requests_count');

  if (error) {
    console.error('Erreur lors de la récupération du nombre de demandes:', error);
    return 0;
  }

  return data || 0;
}

/**
 * Approuve une demande d'autorisation
 */
export async function approveDeviceRequest(
  requestId: string,
  reviewerId: string,
  deviceName?: string
): Promise<void> {
  const { error } = await supabase.rpc('approve_device_request', {
    p_request_id: requestId,
    p_reviewer_id: reviewerId,
    p_device_name: deviceName || null,
  });

  if (error) {
    console.error("Erreur lors de l'approbation de la demande:", error);
    throw error;
  }
}

/**
 * Rejette une demande d'autorisation
 */
export async function rejectDeviceRequest(
  requestId: string,
  reviewerId: string,
  notes?: string
): Promise<void> {
  const { error } = await supabase.rpc('reject_device_request', {
    p_request_id: requestId,
    p_reviewer_id: reviewerId,
    p_notes: notes || null,
  });

  if (error) {
    console.error('Erreur lors du rejet de la demande:', error);
    throw error;
  }
}

/**
 * Récupère tous les appareils autorisés pour un utilisateur
 */
export async function getUserDevices(userId: string): Promise<AuthorizedDevice[]> {
  const { data, error } = await supabase
    .from('authorized_devices')
    .select('*')
    .eq('user_id', userId)
    .order('last_used_at', { ascending: false });

  if (error) {
    console.error('Erreur lors de la récupération des appareils:', error);
    throw error;
  }

  return data || [];
}

/**
 * Récupère tous les appareils autorisés (pour admin/gérant)
 */
export async function getAllDevices(): Promise<AuthorizedDevice[]> {
  const { data, error } = await supabase
    .from('authorized_devices')
    .select('*')
    .order('last_used_at', { ascending: false });

  if (error) {
    console.error('Erreur lors de la récupération des appareils:', error);
    throw error;
  }

  return data || [];
}

/**
 * Révoque un appareil autorisé
 */
export async function revokeDevice(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('authorized_devices')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', deviceId);

  if (error) {
    console.error("Erreur lors de la révocation de l'appareil:", error);
    throw error;
  }
}

/**
 * Réactive un appareil
 */
export async function reactivateDevice(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('authorized_devices')
    .update({ is_active: true, updated_at: new Date().toISOString() })
    .eq('id', deviceId);

  if (error) {
    console.error("Erreur lors de la réactivation de l'appareil:", error);
    throw error;
  }
}

/**
 * Supprime définitivement un appareil
 */
export async function deleteDevice(deviceId: string): Promise<void> {
  const { error } = await supabase.from('authorized_devices').delete().eq('id', deviceId);

  if (error) {
    console.error("Erreur lors de la suppression de l'appareil:", error);
    throw error;
  }
}
